/* eslint-disable react/jsx-indent */
import React from 'react';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// native components
import ClientServicesPhone4644, { clientServicesPhoneNumberLinkUrl } from '../../../../components/clientServicesPhone4644/clientServicesPhone4644';

export default () => (
  <SectionContainer
    id='security-and-privacy-online-safety'
    backgroundVariant='lightA'>
    <Row align='center'>
      <Col medium={10}
        large={8}>
        <Typography
          align='center'
          component='h2'
          nextElement='typography'>
          General security hygiene &#38; suggestions <WidowBlock>for staying safe</WidowBlock>
        </Typography>
        <Typography
          nextElement='typography'>
          Protecting your NM account
        </Typography>
        <BulletedList
          bulletType='filledCircle'
          bulletedListData={[
            {
              bulletText: (<>
                <Typography nextElement='typography'>
                  <strong>Registering for online account access</strong>
                </Typography>
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: (<>When you register for Northwestern Mutual online account access, we will require several pieces of personal information from you. This helps ensure that only you may register to access your own accounts. Avoid accessing your online accounts through publicly shared computers and Wi&#45;Fi networks.</>) },
                    { bulletText: (<><strong>Multi&#45;Factor Authentication:</strong> The company requires users to add an extra layer of protection to their northwesternmutual.com account login process by requiring entry of a security code in addition to their username and password. The security code is a unique, single&#45;use number they receive via phone call, text, or your preferred authenticator app on your mobile device.</>) },
                  ]} />
              </>),
            },
            {
              bulletText: (<>
                <Typography nextElement='typography'>
                  <strong>Passwords</strong>
                </Typography>
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: 'A strong password is important to protect your online accounts. When you are selecting a password, keep the following tips in mind:' },
                    {
                      bulletText: (<>Strong (good) passwords have the following characteristics:
                        {
                          <BulletedList
                            bulletType='filledSquare'
                            bulletedListData={[
                              { bulletText: (<>Contain both upper and lower&#45;case characters (e.g., a&#8211;z, A&#8211;Z)</>) },
                              { bulletText: (<>Have digits and punctuation characters as well as letters (e.g., 0&#8211;9, &#33;&#64;&#35;&#36;&#37;&#94;&#38;&#42;)</>) },
                              { bulletText: 'Fourteen (14) or more alphanumeric characters' },
                              { bulletText: 'Not based on personal information, names of family, or important calendar dates' },
                              { bulletText: 'Change passwords frequently.' },
                              { bulletText: 'Consider using a password manager.' },
                              { bulletText: 'Keep your new password private and change it immediately if someone else learns it or you believe it has been compromised in any way.' },
                              { bulletText: 'Constructing a passphrase based on a sentence or phrase makes a password easy to remember. Passphrase is a type of string password that uses a short sentence or group of random words.' },
                            ]} />
                        }
                      </>),
                    },

                    {
                      bulletText: (<>Weak (bad) passwords have the following characteristics:
                        {
                          <BulletedList
                            bulletType='filledSquare'
                            bulletedListData={[
                              { bulletText: 'Default password' },
                              { bulletText: 'Contain less than eight (8) characters' },
                              {
                                bulletText: (<>Common, easily guessed phrases &#40;e.g. &#34;Winter is coming&#34;&#41;
                                  {
                                    <BulletedList
                                      bulletType='filledCircle'
                                      bulletedListData={[
                                        { bulletText: (<>A common usage word such as names of family, pets, friends, co&#45;workers, fantasy characters, etc. </>) },
                                        { bulletText: (<>The words &#34;Northwestern Mutual&#34; or any derivation</>) },
                                        { bulletText: 'Do not use Social Security numbers, words or numbers associated with easily attainable personal information, like birthdays, anniversaries, license plates, telephone numbers, or addresses.' },
                                        { bulletText: 'Do not use the same pattern for your passwords, such as smart1, smart2, etc.' },
                                        { bulletText: 'Any of the above spelled backward' },
                                        { bulletText: 'Any of the above preceded or followed by a digit (e.g., secret1 or 1secret).' },
                                        { bulletText: 'Do not use real names or your login name or any variation of it.' },
                                        { bulletText: 'Do not write down your password or share your password with anyone else.' },
                                        { bulletText: 'Do not reuse passwords. Make sure you use different and unique passwords for all of your online accounts. Reusing a single password for multiple websites is never a good idea. If a cybercriminal obtains your password, they may try to use it on other websites.' },
                                      ]} />
                                  }
                                </>),
                              },
                              {
                                bulletText: (<>Protecting your account: Be prepared to provide your account&#47;policy number when reaching out to us to set up the following safeguards: {
                                  <BulletedList
                                    bulletType='filledCircle'
                                    bulletedListData={[
                                      { bulletText: (<>Life, Disability Income and Annuity: You can request a password to be used to verify your identity when you call the home office.</>) },
                                      {
                                        bulletText: (<>For Life and Disability Income Policies, call <Link
                                          variant='primaryDeemphasize'
                                          linkUrl='tel:18003888123'
                                          noWrap>
                                          1&#45;800&#45;388&#45;8123
                                        </Link>.</>),
                                      },
                                      {
                                        bulletText: (<>For Annuities, call <Link
                                          variant='primaryDeemphasize'
                                          linkUrl='tel:18884552232'
                                          noWrap>
                                          1&#45;888&#45;455&#45;2232
                                        </Link>.</>),
                                      },
                                      {
                                        bulletText: (<>Long&#45;Term Care: You can request additional authenticators be added to verify your identity when calling about your policy by calling <Link
                                          variant='primaryDeemphasize'
                                          linkUrl='tel:18007489493'
                                          noWrap>
                                            1&#45;800&#45;748&#45;9493
                                        </Link>.</>),
                                      },
                                      { bulletText: (<>Investment Accounts: You can request a note or block be added to your account.</>) },
                                      {
                                        bulletText: (<>
                                        For Northwestern Mutual Investment Services and Northwestern Mutual Wealth Management Company, including Trust and Private Client Services, call{' '}
                                          <Link
                                            id='online-safety-client-services-phone-link'
                                            variant='primaryDeemphasize'
                                            linkUrl={clientServicesPhoneNumberLinkUrl}
                                            noWrap>
                                            <ClientServicesPhone4644 />
                                          </Link>
                                          {' '}and ask for &#34;investments&#34;.
                                        </>),
                                      },
                                    ]} />
                                }
                                </>),
                              },
                            ]} />
                        }
                      </>),
                    },

                  ]} />
              </>),
            },
          ]}
        />
        <Typography
          component='h3'
          nextElement='typography'>
         Staying safe online
        </Typography>
        <BulletedList
          bulletType='filledCircle'
          bulletedListData={[
            {
              bulletText: (<><strong>Email Hacking:</strong> Email hacking occurs when a cybercriminal illegally gains access to an individual&#39;s email account. This allows the hacker to read email messages and view the address book on the email account. Using this information, the cybercriminal (appearing to be the individual), contacts the individual&#39;s financial institutions via an email message and tries to obtain funds. Learn about how to protect yourself at <Link
                variant='primaryDeemphasize'
                linkUrl='https://www.northwesternmutual.com/anti-fraud-efforts/#anti-fraud-efforts-email-hacking-fraud-section-container'>Email Hacking Fraud</Link>. When email hacking occurs, emails can&#47;will be intercepted and deleted by the hacker, and the rightful sender and receiver will no longer see legitimate email traffic. We recommend that you take the following actions to protect yourself:
              {
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: 'Never give your email address to anyone or any site that you do not trust.' },
                    { bulletText: 'Never send personal or sensitive information in an unsecured email.' },
                  ]} />
              }
              </>),
            },
            { bulletText: (<><strong>Phishing:</strong> One of the most common ways cybercriminals trick their victims is through phishing. This occurs when a cybercriminal tries to either get the victim to reveal confidential information or installs malicious software (malware) on the victim&#39;s computer. A phishing attack can take many forms, although the most common is an email message.</>) },
            {
              bulletText: (<><strong>Identifying phishing messages:</strong> common phishing message characteristics and red flags include:
                {
                  <BulletedList
                    bulletType='disc'
                    bulletedListData={[
                      { bulletText: (<><strong>Unsolicited email or attachments:</strong> Always regard unsolicited email or attachments as suspicious, even if the message appears to come from a known sender. The &#34;From&#34; address in emails can be easily faked. In addition, the sender&#39;s email account may have been hacked. </>) },
                      { bulletText: (<><strong>Spoofing:</strong> Email spoofing refers to email messages that are sent with a forged sender address. The email is sent to look like it is from a known company or people such as your friend, relative, or co&#45;worker, but the email is fake.</>) },
                      { bulletText: (<>If you receive an email asking for confidential information and demanding unusual or urgent action, be suspicious. Take action against this and call the sender directly, using contact information on record, to ask if they sent the email.</>) },
                      { bulletText: (<><strong>Generic greetings:</strong> Phishing messages often contain generic, non&#45;personalized greetings both in the subject line and the message. Some legitimate messages may contain generic greetings, but a generic greeting should raise suspicions. It&#39;s important to note, though, that a personalized greeting in and of itself does not guarantee that a message is legitimate.</>) },
                      { bulletText: (<><strong>Urgent or threatening language</strong>: Many phishing messages contain urgent or threatening language. Phishers often try to manipulate people&#39;s emotions. Don&#39;t fall for that trick. Take the time to examine unsolicited messages carefully.</>) },
                      { bulletText: (<><strong>Awkward grammar or spelling errors:</strong> Awkward grammar or spelling errors may be signs of phishing messages. However, don&#39;t assume that polished, professional&#45;looking messages are necessarily legitimate.</>) },
                      { bulletText: (<><strong>Tricky links:</strong> Always be suspicious of links in unsolicited messages. The web address displayed in the message can be easily faked. Hover the mouse pointer over the link on a computer or long&#45;press the link on a mobile device to preview the actual link address.</>) },
                      { bulletText: (<>Never send personal or sensitive information in an unsecured (unencrypted) email.</>) },
                      { bulletText: (<>Never make a purchase from an unsolicited email. Not only can an email user fall prey to a potentially fraudulent sales scheme, but their email address may be added to email lists sold within the spamming community, further compounding the number of unsolicited emails the user receives.</>) },
                    ]} />
                }
              </>),
            },
            {
              bulletText: (<><strong>Phishing resources:</strong> To learn more about phishing and handling phishing messages, see the <Link
                variant='primaryDeemphasize'
                linkUrl='https://consumer.ftc.gov/articles/how-recognize-and-avoid-phishing-scams/'
                isOutgoingLink>
                Federal Trade Commission (FTC) website
              </Link>.</>),
            },
            {
              bulletText: (<><strong>Spam:</strong> Spam is defined as unwanted or unsolicited email. What can you do to reduce spam emails&#63; If the volume of spam email is low, the easiest thing to do is simply delete the spam messages. Consider following these steps to prevent or reduce spam:
                {
                  <BulletedList
                    bulletType='disc'
                    bulletedListData={[
                      { bulletText: (<>It is okay to &#34;unsubscribe&#34; from something you signed up for. However, if the newsletter or email is unsolicited or suspicious, safely unsubscribe by going directly to the site through your browser, rather than clicking a link in the email.</>) },
                      { bulletText: 'Never provide your email address to a website, app, or service you do not trust.' },
                      { bulletText: (<>If you opt&#45;in (sign up) on a website to receive email marketing, deselect checked boxes that opt you in to receive additional related, but possibly unwanted email.</>) },
                      { bulletText: 'Check with your email providers to determine how to report an unsolicited message.' },
                      {
                        bulletText: (<>For additional information, refer to the <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://consumer.ftc.gov/articles/how-recognize-and-avoid-phishing-scams/'
                          isOutgoingLink>
                      FTC website.</Link></>),
                      },
                    ]} />
                }
              </>),
            },
            { bulletText: (<><strong>Use a password manager:</strong> Password management software, a virtual space that allows you to safely store your account usernames and passwords, can help simplify choosing and maintaining passwords for your online accounts. Make sure to keep your passwords updated within your password manager. Several password management applications are available for a variety of devices and operating systems. Check with a trusted technology expert to help you choose the appropriate password manager.</>) },
            {
              bulletText: (<><strong>Social media safety:</strong> social media includes websites and mobile apps that allow you to stay connected with friends and family&#8212;but be careful what you share. Cybercriminals could use the confidential details that you share publicly to conduct identity theft. A few tips will help you stay secure on these sites:
                {
                  <BulletedList
                    bulletType='disc'
                    bulletedListData={[
                      { bulletText: 'Most social media sites offer privacy settings and other tools to help you restrict who sees your content. Learn how these settings and tools work for each site and be aware of any updates to how they work.' },
                      { bulletText: 'Realize that cybercriminals can use social media to trick their victims. If a contest seems too good to be true, it most likely is. Links to the latest celebrity gossip, personality quizzes or shocking pictures can lead to malicious software (malware) or sites designed to steal confidential information.' },
                      { bulletText: (<>Limit the information that you share online such as where you attend school, children&#39;s ages, details about your work or family or other personally identifiable information. </>) },
                      { bulletText: 'Be careful what images you and your family are sharing, especially if you are on vacation and away from home. You could alert others that your home is unattended.' },
                      { bulletText: 'Remember that any information you post online can be saved and accessed forever.' },
                      {
                        bulletText: (<>More information about social media safety is available at the <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://staysafeonline.org/'
                          isOutgoingLink>
                      National Cybersecurity Alliance</Link>.</>),
                      },
                    ]} />
                }
              </>),
            },
            {
              bulletText: (<><strong>Keeping your kids safe online:</strong> Talking with your children about being smart and safe online is one of the best ways you can protect them from harm. Begin by talking early and often about what is an acceptable online activity. Learn from your children by having them show you what they do online, stay calm and listen carefully if there is something online that makes them uncomfortable. If you don&#39;t know where to start, you&#39;re not alone. Visit <Link
                variant='primaryDeemphasize'
                linkUrl='https://www.missingkids.org/NetSmartz/home/'
                isOutgoingLink>
                  NetSmartz</Link> for more tips and discussion starters. If you are wondering whether your child is being exposed to age&#45;inappropriate materials, <Link
                variant='primaryDeemphasize'
                linkUrl='https://www.commonsensemedia.org/'
                isOutgoingLink>CommonSenseMedia</Link> provides ratings for a variety of media. Children may become victims of cyber&#45;bullying. Depending on the severity of the issue, contact the social media site, school officials, or law enforcement to report the incident. You may want to monitor your child&#39;s social media activity and review their friends list to ensure they are communicating responsibly. Have your kids follow these tips to stay safe online. Instruct them:
              {
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: 'Never to post personally identifiable information about themselves such as address, phone, school, or email address.' },
                    { bulletText: 'Not to tell anyone their usernames or passwords except for parents or guardians.' },
                    { bulletText: 'Not to open emails from strangers.' },
                    { bulletText: (<>Never to arrange to meet someone in&#45;person who they met online.</>)  },
                    { bulletText: 'That people are not always who they say they are.' },
                    { bulletText: (<>Not to click on pop&#45;ups, even if they say that you will win a prize.</>) },
                    { bulletText: 'What you put online may never be truly private and may stay online forever.' },
                    { bulletText: 'If someone puts something online that makes them feel sad or scared, log off and tell someone. When in doubt, always ask a parent, guardian, or teacher.' },
                    { bulletText: (<>And most important: Get off the computer and put down the phone because nothing beats spending time with friends in&#45;person.</>) },
                  ]} />
              }
              </>),
            },
            { bulletText: (<><strong>Parental controls:</strong> Parental controls are available on most internet&#45;enabled devices, like computers, smartphones, tablets, and gaming systems. When enabling parental controls, use age&#45;appropriate settings to filter, monitor and block your child&#39;s activities. Work with a trusted technology expert if you have questions.</>) },
          ]}
        />
        <Typography
          nextElement='typography'
          component='h3'
          align='left'>
          Secure your device
        </Typography>
        <BulletedList
          bulletType='filledCircle'
          bulletedListData={[
            {
              bulletText: (<>
                <Typography nextElement='typography'>
                  <strong>Antivirus&#47;anti&#45;malware:</strong> Protect your computer from malicious software (malware) by installing and running up&#45;to&#45;date malware protection. A variety of options are available online or at local retail stores. Work with a trusted technology expert if you have questions.
                </Typography>
              </>),
            },
            { bulletText: (<><strong>Operating systems:</strong> To remain secure online, update your operating system (the system that manages the hardware and software on your computer and mobile devices) frequently. Consider activating automated updates if available. Apple, Google, Microsoft and other operating system vendors frequently update their operating systems. These updates may add functionality, increase security, and fix problems in existing software. Apps: Be cautious when downloading apps. Some apps may contain malware designed to steal your personal and financial information. Make sure that the updates or downloads come from the company that originally released the software. Safely download apps only from reputable, approved sources like the Apple App Store or Google Play. To protect your privacy, review permissions at the time of installation or update to decide if you are comfortable granting access requested by that app. Other applications also typically offer automatic update options. Turning on auto updates will ensure you always have the latest software version available. It&#39;s important to make sure you keep your applications updated to protect from vulnerabilities and increase functionality of services provided.</>) },
            {
              bulletText: (<><strong>Using GPS on your mobile device:</strong> Your device&#39;s built&#45;in global positioning system (GPS) locates and publishes information about your whereabouts. For example, apps may allow you to check in at places using your mobile device, and then share your location on social networks. Here are some tips to use GPS location services safely:
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: 'Turn off GPS on your mobile device when you do not need it or only allow certain apps to use your location data. Refer to your mobile device manual for further instructions on how to adjust this feature.' },
                    { bulletText: <>Know that what you share on one site may be linked to another site (such as social media sites).</> },
                    { bulletText: 'Check the privacy settings on all your accounts. Make sure you are only sharing information with people you know.' },
                    {
                      bulletText: (<>Remember when taking pictures with your mobile device that location information (known as geotagging) may be embedded in the photo. For more information visit <Link
                        variant='primaryDeemphasize'
                        linkUrl='https://staysafeonline.org/'
                        isOutgoingLink>
                        National Cybersecurity Alliance
                      </Link>.</>),
                    },
                  ]} /></>),
            },
            {
              bulletText: (<><strong>Physical security of mobile devices:</strong> Mobile devices require additional protection. Treat your mobile devices as you would your wallet. Consider the following best practices to keep your mobile devices secure:
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: (<>Always lock your screen when not in use. Locking your screen is a simple yet important thing you can do to ensure security on your mobile device, especially if it&#39;s lost or stolen.</>) },
                    { bulletText: <>Beware of shoulder surfers: thieves who physically watch your onscreen activities to steal your confidential information or passwords. Pay attention to your surroundings and leave if you are uncomfortable.</> },
                    { bulletText: 'Never leave your mobile device unattended.' },
                    { bulletText: (<>If you are not able to keep your device with you, lock your mobile device in a secure location. If you need to leave your mobile device in your vehicle, lock it in the trunk out of sight; don&#39;t leave it in the passenger compartment.</>) },
                  ]} />
              </>),
            },
            { bulletText: (<><strong>Web browsers:</strong> It is important to keep your web browsers up to date to correct any bugs or vulnerabilities that older versions may have. Download the latest version of your web browser. If your web browser supports automatic updating, consider turning on that feature to ensure you always have the latest version.</>) },
            {
              bulletText: (<><strong>Wi&#45;Fi security:</strong> Wi&#45;Fi allows you to wirelessly connect to the internet. The following tips can help you remain safe when you use Wi&#45;Fi networks:
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: (<>Using a wireless network at home or using other internet enabled devices such as baby monitors, cameras, and thermostats is convenient, but leaving them unsecured is an opportunity for cybercriminals to hack in and discover sensitive information. Do not use any default settings or passwords provided by the manufacturer. Make sure to add a unique passcode so that you and your family are the only ones accessing these devices.</>) },
                    { bulletText: (<>Realize that public Wi&#45;Fi networks are not secure. Other people on the network may be able to view the information you send and receive unless that information is encrypted. If you need to connect to public Wi&#45;Fi, consider using a trusted virtual private network (VPN) provider to encrypt all network traffic.</>) },
                  ]} />
              </>),
            },
            {
              bulletText: (<><strong>Website security</strong>
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: (<>If you&#39;re performing a transaction or sharing confidential information through a website, make sure the site begins with https:&#47;&#47; to ensure that the information will be secure during the transmission between your browser and the website.</>) },
                    { bulletText: (<>If a site begins with anything other than https:&#47;&#47;, your information may be visible to other people. Never communicate confidential information through those types of sites.</>) },
                    { bulletText: (<>It&#39;s always best to go to a website via your saved favorites or by entering the website&#39;s URL in your browser window. Clicking on a link to a website inside an email or on a pop&#45;up that displays on your screen could take you to a malicious site.</>) },
                    { bulletText: (<>Always log out of your accounts when you are finished if you&#39;re accessing those accounts from a shared device.</>) },
                  ]} />
              </>),
            },
            { bulletText: (<><strong>Managing your records and information:</strong> Managing your records and information appropriately will help keep you organized and in control of your confidential information. Properly disposing of your records when you no longer need them will help protect your confidential information from falling into the wrong hands.</>) },
            { bulletText: (<><strong>Records and information management:</strong> Government agencies like the Federal Trade Commission offer guidance on managing family and household records. Review the appropriate government agency&#39;s information to determine how long you should keep important documents. You should be securely disposing of confidential information on a regular basis.</>) },
            {
              bulletText: (<><strong>Secure disposal:</strong> Properly disposing of your records will help prevent criminals sorting through your trash to locate your confidential information. Here are some tips for you to consider:
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    { bulletText: 'Always shred your confidential information. Use a crosscut shredder that cuts the documents into small pieces.' },
                    { bulletText: <>Shredders that cut documents into long spaghetti&#45;like strands are not as secure. Properly motivated criminals can reconstruct those strands with enough effort.</> },
                    { bulletText: (<>Shred DVDs, CDs, diskettes, tapes, and credit cards if possible. High&#45;end shredders often have the capability to shred these items. Always confirm that a shredder can accommodate the items you want to shred.</>) },
                    { bulletText: 'Before you sell a smartphone or mobile device, ensure the device is fully encrypted and then perform a factory reset to remove confidential information from the device.' },
                    { bulletText: (<>Use secure erase software to wipe, or electronically &#34;shred,&#34; information on a personal computer&#39;s hard drive. At a minimum, take the hard drive out of a laptop or desktop computer before disposing of the old computer.</>) },
                    { bulletText: 'Regularly delete emails from your Inbox and Deleted Items folder.' },
                  ]} />
              </>),
            },
          ]} />
        <Typography
          nextElement='typography'
          component='h3'>
          Steps to take if you believe you&#39;re <WidowBlock>a victim</WidowBlock>
        </Typography>
        <BulletedList
          disableBottomPadding
          bulletType='filledCircle'
          bulletedListData={[
            {
              bulletText: (<>
                <Typography nextElement='typography'>
                  <strong>Identity theft</strong>
                </Typography>
                <BulletedList
                  bulletType='disc'
                  disableBottomPadding
                  bulletedListData={[
                    {
                      bulletText: (<>
                          Get a copy of your credit report and review the accounts and other information provided. You can get a free credit report from
                        <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://www.annualcreditreport.com/'
                          isOutgoingLink> Annual Credit Report</Link> or by calling <Link
                          variant='primaryDeemphasize'
                          linkUrl='tel:18773228228'
                          noWrap>1&#45;877&#45;322&#45;8228</Link>. You&#39;re entitled to one free report from each of the credit reporting agencies every year.
                      </>),
                    },
                    { bulletText: 'Review the account statements you receive each month from the banks and credit card companies you work with, and report anything that looks suspicious. If applicable, you are also entitled to receive copies of police reports if any have been filed.' },
                  ]} />
              </>),
            },
            {
              bulletText: (<>
                <Typography nextElement='typography'>
                  <strong>Fraud alert</strong>
                </Typography>
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    {
                      bulletText: (<>You may ask that an initial fraud alert be placed on your credit report if you suspect you have been or may become a victim of identity theft. An initial fraud alert stays on your credit report for one year.
                        <BulletedList
                          bulletType='filledSquare'
                          bulletedListData={[
                            { bulletText: 'You may have an extended alert placed on your credit report if you have already been a victim of identity theft and you have the appropriate documentary proof. An extended fraud alert stays on your credit report for seven years.' },
                            {
                              bulletText: (<>You can place a fraud alert on your credit report by calling the toll&#45;free fraud number of any of the three national credit reporting agencies listed below.
                                <BulletedList
                                  bulletType='filledCircle'
                                  bulletedListData={[
                                    {
                                      bulletText: (<>Equifax: <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='tel:18667660008'>
                                        1&#45;866&#45;766&#45;0008
                                      </Link>, <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='https://www.equifax.com/'
                                        isOutgoingLink>equifax.com</Link></>),
                                    },
                                    {
                                      bulletText: (<>Experian: <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='tel:18883973742'>
                                        1&#45;888&#45;397&#45;3742
                                      </Link>, <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='https://www.experian.com/'
                                        isOutgoingLink>experian.com</Link></>),
                                    },
                                    {
                                      bulletText: (<>TransUnion: <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='tel:18006807289'>
                                        1&#45;800&#45;680&#45;7289
                                      </Link>, <Link
                                        variant='primaryDeemphasize'
                                        linkUrl='https://service.transunion.com/dss/login.page?dest=fraudAlert/'
                                        isOutgoingLink>fraud.transunion.com</Link></>),
                                    },
                                  ]}
                                />
                              </>),
                            },
                          ]}
                        />
                      </>),
                    },
                    {
                      bulletText: (<><Link
                        variant='primaryDeemphasize'
                        linkUrl='/anti-fraud-efforts/'>Anti&#45;fraud efforts
                      </Link></>),
                    },
                  ]}
                />
              </>),
            },
            {
              bulletText: (<><strong>Credit Freeze</strong>
                <BulletedList
                  bulletType='disc'
                  bulletedListData={[
                    {
                      bulletText: (<>
                      You have the right to put a credit freeze, also known as a security freeze, on your credit file, so that no new credit can be opened in your name without the use of a personal identification number (PIN) that is issued to you when you initiate a freeze. A credit freeze is designed to prevent potential credit grantors from accessing your credit report without your consent. Federal law allows you to freeze and unfreeze your credit at the three major credit bureaus without charge.
                      </>),
                    },
                    { bulletText: 'If you place a credit freeze, potential creditors and other third parties will not be able to get access to your credit report unless you temporarily lift the freeze. Therefore, using a credit freeze may delay your ability to obtain credit.' },
                    {
                      bulletText: (<>
                        Unlike a fraud alert, you must place a credit freeze on your credit file at each credit reporting company at the addresses below:
                        <BulletedList
                          bulletType='filledSquare'
                          bulletedListData={[
                            {
                              bulletText: (<>
                                Equifax: <address>P.O. Box 105788, Atlanta, GA 30348</address>
                                <Link
                                  variant='primaryDeemphasize'
                                  linkUrl='https://www.equifax.com/'
                                  isOutgoingLink>equifax.com</Link>
                              </>),
                            },
                            {
                              bulletText: (<>
                                  Experian: <address>P.O. Box 9554, Allen, TX 75013</address>
                                <Link
                                  variant='primaryDeemphasize'
                                  linkUrl='https://www.experian.com/'
                                  isOutgoingLink>experian.com</Link>
                              </>),
                            },
                            {
                              bulletText: (<>
                                  TransUnion LLC: <address>P.O. Box 2000, Chester, PA 19022&#8211;2000</address> <Link
                                  variant='primaryDeemphasize'
                                  linkUrl='https://service.transunion.com/dss/login.page?dest=fraudAlert/'
                                  isOutgoingLink>fraud.transunion.com</Link>
                              </>),
                            },
                          ]} />
                      </>),
                    },
                  ]} />
              </>),
            },
            {
              bulletText: (<><strong>Additional Information</strong>
                <BulletedList
                  disableBottomPadding
                  bulletType='filledSquare'
                  bulletedListData={[
                    {
                      bulletText: (<>
                          For tips and other helpful information, contact the Federal Trade Commission (FTC) at <Link
                          linkUrl='tel:12023266000'
                          variant='primaryDeemphasize'>1&#45;877&#45;ID&#45;THEFT</Link> or <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://consumer.ftc.gov/features/identity-theft/'
                          isOutgoingLink>
                          FTC website</Link>. You may also contact your state Attorney General. For contact information, call the National Association of Attorney Generals at <Link
                          variant='primaryDeemphasize'
                          linkUrl='tel:12023266000'>
                          1&#45;202&#45;326&#45;6000</Link> or go to
                        <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://www.naag.org/'
                          isOutgoingLink> naag.org
                        </Link>.
                      </>),
                    },
                    {
                      bulletText: (<>
                          You can request an <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://www.irs.gov/identity-theft-fraud-scams/get-an-identity-protection-pin/'
                          isOutgoingLink>
                          IRS Identity Protection PIN</Link> and, in many states, a state PIN for income tax filing purposes. You can also lock your social security number (SSN) from any changes through <Link
                          variant='primaryDeemphasize'
                          linkUrl='https://myeverify.uscis.gov/'
                          isOutgoingLink
                          disableBottomPadding>
                          myE&#45;Verify</Link>.
                      </>),
                    },

                  ]}
                />
              </>),
            },
          ]} />
      </Col>
    </Row>
  </SectionContainer>
);

